// @ts-check

import { api } from '@/api'
import { useMemoize } from '@vueuse/core'

/**
 * Creates a new payslip input
 *
 * @param {Pick<PayslipInput, 'recipient_iban' | 'recipient_ssn' | 'recipient_first_name' | 'recipient_last_name' | 'period_start' | 'period_end' | 'payment_date'>} payload
 * @returns {Promise<PayslipInput>}
 */
export const createPayslipInput = async (payload) => {
  const res = await api.post('/payslips/input', payload)
  return res.data
}

/**
 * Gets a payslip input
 *
 * @param {PayslipInput['id']} inputId
 * @returns {Promise<PayslipInput>}
 */
export const getPayslipInput = async (inputId) => {
  const res = await api.get(`/payslips/input/${inputId}`)
  return res.data
}

/**
 * Updates the tax card info of a payslip input
 *
 * @param {PayslipInput['id']} inputId
 * @param {Pick<PayslipInput, 'additional_withholding_percent' | 'withholding_percent' | 'income_tax_card_date' | 'income_limit'>} payload
 * @returns {Promise<PayslipInput>}
 */
export const updatePayslipInputTaxInfo = async (inputId, payload) => {
  const res = await api.put(`/payslips/input/${inputId}/tax-card`, payload)
  return res.data
}

/**
 * Updates the monetary wage of a payslip input
 *
 * @param {PayslipInput['id']} inputId
 * @param {Pick<PayslipInput, 'monetary_wage'>} payload
 * @returns {Promise<PayslipInput>}
 */
export const updatePayslipInputMonetaryWage = async (inputId, payload) => {
  const res = await api.put(`/payslips/input/${inputId}/monetary-wage`, payload)
  return res.data
}

/**
 * Updates the employee details of a payslip input
 *
 * @param {PayslipInput['id']} inputId
 * @param {Pick<PayslipInput, 'recipient_iban' | 'recipient_ssn' | 'recipient_first_name' | 'recipient_last_name' | 'period_start' | 'period_end' | 'payment_date'>} payload
 * @returns {Promise<PayslipInput>}
 */
export const updatePayslipInputEmployee = async (inputId, payload) => {
  const res = await api.put(
    `/payslips/input/${inputId}/person-details`,
    payload
  )
  return res.data
}

/**
 * Updates the employee details of a payslip input
 *
 * @param {PayslipInput['id']} inputId
 * @param {Address} payload
 * @returns {Promise<PayslipInput>}
 */
export const updatePayslipInputAddress = async (inputId, payload) => {
  const res = await api.put(
    `/payslips/input/${inputId}/person-address`,
    payload
  )
  return res.data
}

/**
 * Updates the benefits of a payslip input
 *
 * @param {PayslipInput['id']} inputId
 * @param {PayslipInput['benefits']} payload
 * @returns {Promise<PayslipInput>}
 */
export const updatePayslipInputBenefits = async (inputId, payload) => {
  const res = await api.patch(`/payslips/input/${inputId}/benefits`, payload)
  return res.data
}

/**
 * Submits a payslip input
 *
 * @param {PayslipInput['id']} inputId
 * @returns {Promise<PayslipInput>}
 */
export const submitPayslipInput = async (inputId) => {
  const res = await api.post(`/payslips/input/${inputId}/send`)
  return res.data
}

/**
 * Deletes a payslip input
 *
 * @param {PayslipInput['id']} inputId
 */
export const deletePayslipInput = async (inputId) =>
  api.delete(`/payslips/input/${inputId}`)

/**
 * Gets all available benefits for payslip inputs.
 *
 * @param {string} language
 * @returns {Promise<AvailablePayslipInputBenefit[]>}
 */
export const getPayslipInputBenefits = useMemoize(async (language) => {
  const res = await api.get(`/payslips/input/available-benefits`, {
    params: {
      language
    }
  })
  return res.data
})

/**
 * Invalidates a payslip input.
 *
 * @param {PayslipInput['id']} inputId
 */
export const invalidatePayslipInput = async (inputId) =>
  api.post(`/payslips/input/${inputId}/invalidate`)

/**
 * Previews a payslip input.
 *
 * @param {PayslipInput['id']} inputId
 * @param {?string} language
 * @returns {Promise<PayslipItem[]>}
 */
export const previewPayslipInput = async (inputId, language) => {
  const res = await api.post(
    `payslips/input/${inputId}/calculate-preview`,
    undefined,
    {
      params: {
        language
      }
    }
  )
  return res.data
}

/**
 * Gets the expenses of a payslip input.
 *
 * @param {PayslipInput['id']} inputId
 * @returns {Promise<any[]>}
 */
export const getPayslipInputExpenses = async (inputId) => {
  const res = await api.get(`/payslips/input/${inputId}/expenses`)
  return res.data
}
